import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { HEADER_ID } from '@/components/templates/PageLayout';
import { ORDER_METHOD, OrderMethod } from '@/domain/models/Order/orderMethod';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { getClient } from '@/lib/restClient';
import {
  ItemFormError,
  validateAvailableOrderMethods,
  validateConditionValue,
  validateCouponCode,
  validateDiscount,
  validateMaxUsagePerUser,
  validateName,
  validateValidDateRange,
} from '@/pages/camel-order/coupons/create/validate';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { CamelOrderCouponService } from '@/services/CamelOrder/CamelOrderCouponService';

export type Page = 'new' | 'edit';
export type Status = 'ACTIVE' | 'INACTIVE';
export type IsAuthRequired = '0' | '1';

export const useCamelOrderCouponItemForm = () => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('camel_order_site_setting', history, managerAccountMe);
  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState<ItemFormError>({
    name: { ...errorObj },
    couponCode: { ...errorObj },
    discount: { ...errorObj },
    conditionValue: { ...errorObj },
    maxUsagePerUser: { ...errorObj },
    availableOrderMethod: {
      ...errorObj,
    },
    validFrom: { ...errorObj },
    validTo: { ...errorObj },
    status: { ...errorObj },
  });
  const { site_id } = useParams<{ site_id: string }>();
  const [name, setName] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [discount, setDiscount] = useState<string | undefined>(undefined);
  const [conditionType] = useState<string>('MINIMAL_ORDER_VALUE');
  const [conditionValue, setConditionValue] = useState<string>('');
  const [maxUsagePerUser, setMaxUsagePerUser] = useState<string>('');
  const [validFrom, setValidFrom] = useState<Date | null>(null);
  const [validTo, setValidTo] = useState<Date | null>(null);
  const [status, setStatus] = useState<string>('ACTIVE');
  const [selectedAvailableOrderMethod, setSelectedAvailableOrderMethod] =
    useState<OrderMethod[]>([
      ORDER_METHOD.TAKE_OUT,
      ORDER_METHOD.EAT_IN,
      ORDER_METHOD.DELIVERY,
    ]);
  const listUrl = routes.camelOrderCoupons.path;

  const initializeNewPageState = async () => {
    setStatus('ACTIVE');
  };

  const handleCreate = async () => {
    try {
      const params: CamelOrderCouponCreateItemParams = {
        name,
        site_id: site_id,
        code: couponCode,
        type: 'ORDER',
        discount_type: 'FIXED_AMOUNT',
        discount: discount === '' ? null : Number(discount),
        condition_type: conditionValue === '' ? 'NONE' : 'MINIMAL_ORDER_VALUE',
        condition_value: conditionValue === '' ? null : Number(conditionValue),
        max_usage_per_user:
          maxUsagePerUser === '' ? null : Number(maxUsagePerUser),
        valid_from: validFrom,
        valid_to: validTo,
        delivery: selectedAvailableOrderMethod.includes(ORDER_METHOD.DELIVERY),
        takeout: selectedAvailableOrderMethod.includes(ORDER_METHOD.TAKE_OUT),
        eatin: selectedAvailableOrderMethod.includes(ORDER_METHOD.EAT_IN),
        status,
      };
      await useCamelOrderCouponCreateItem(params);
      alert('クーポンを登録しました');
      history.push(listUrl);
    } catch (error) {
      if (error instanceof Error) {
        errorHandler({ error });
      } else {
        errorHandler({ error: new Error('エラーが発生しました') });
      }
    }
  };

  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const newError = validateName(newValue, error);
    setError(newError);
    setName(newValue);
  };

  const handleInputCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const newError = validateCouponCode(newValue, error);
    setError(newError);
    setCouponCode(newValue);
  };

  const handleInputDiscount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const newError = validateDiscount(newValue, error);
    setError(newError);
    const value = newValue === '' ? undefined : newValue;
    setDiscount(value);
  };
  const handleInputValidFrom = (date: Date | null) => {
    const newError = validateValidDateRange(date, validTo, error);
    setError(newError), setValidFrom(date);
  };
  const handleInputValidTo = (date: Date | null) => {
    const newError = validateValidDateRange(validFrom, date, error);
    setError(newError), setValidTo(date);
  };

  const handleInputConditionValue = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;
    const newError = validateConditionValue(newValue, error);
    setError(newError);
    setConditionValue(newValue);
  };

  const handleInputMaxUsagePerUser = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;
    const newError = validateMaxUsagePerUser(newValue, error);
    setError(newError);
    setMaxUsagePerUser(newValue);
  };

  const handleChangeAvailableOrderMethods = (
    orderMethod: OrderMethod,
    flag: boolean
  ) => {
    validateAvailableOrderMethods(
      { selectedOrderMethod: orderMethod, flag },
      selectedAvailableOrderMethod,
      error
    );
    if (flag) {
      if (selectedAvailableOrderMethod.includes(orderMethod)) {
        return;
      }
      setSelectedAvailableOrderMethod((currentState) => [
        ...currentState,
        orderMethod,
      ]);
    } else {
      if (!selectedAvailableOrderMethod.includes(orderMethod)) {
        return;
      }

      setSelectedAvailableOrderMethod((currentState) =>
        currentState.filter((method) => method !== orderMethod)
      );
    }
    setError(error);
  };

  const handleInputStatus = (value: React.ReactText) => {
    setStatus(value as Status);
  };
  return {
    name,
    couponCode,
    discount,
    conditionType,
    conditionValue,
    maxUsagePerUser,
    validFrom,
    validTo,
    status,
    selectedAvailableOrderMethod,
    handleChangeAvailableOrderMethods,
    initializeNewPageState,
    error,
    handleCreate,
    handleInputName,
    handleInputCode,
    handleInputDiscount,
    handleInputValidFrom,
    handleInputValidTo,
    handleInputConditionValue,
    handleInputMaxUsagePerUser,
    handleInputStatus,
    targetsHeight,
  };
};

export type CamelOrderCouponCreateItemParams = {
  name: string;
  site_id: string;
  code: string;
  type: string;
  discount_type: string;
  discount: number | null;
  condition_type: string;
  condition_value: number | null;
  max_usage_per_user: number | null;
  takeout: boolean;
  eatin: boolean;
  delivery: boolean;
  valid_from: Date | null;
  valid_to: Date | null;
  status: string;
};

export type CamelOrderCouponUpdateItemParams = {
  status: 'ACTIVE' | 'INACTIVE';
};

export const useCamelOrderCouponCreateItem = async (
  params: CamelOrderCouponCreateItemParams
): Promise<void> => {
  const camelOrderCouponService = new CamelOrderCouponService(getClient());
  await camelOrderCouponService.create(params);
};
